import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { ArrowRightLeft, Plus } from 'lucide-react';

const TRANSFER_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  awaiting_bank_proof: 'bg-orange-100 text-orange-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const TRANSFER_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  awaiting_bank_proof: 'Awaiting Bank Proof',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const TRANSFER_TYPE_LABELS: Record<string, string> = {
  ownership_transfer: 'Ownership Transfer',
  unit_swap: 'Unit Swap',
  cancellation: 'Cancellation',
};

export function BookingTransfers() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    booking_id: '', transfer_type: '', from_unit: '', to_unit: '', notes: '',
  });

  const { data: bookings } = useQuery({
    queryKey: ['bookings-for-transfer', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('bookings')
        .select('id, customer_name, plot_number, project_id, projects(name)')
        .eq('developer_user_id', user!.id)
        .in('booking_status', ['booked', 'confirmed']);
      return data || [];
    },
    enabled: !!user,
  });

  const { data: transfers, isLoading } = useQuery({
    queryKey: ['booking-transfers', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('booking_transfers')
        .select('*, bookings(customer_name, plot_number, projects(name))')
        .eq('developer_user_id', user!.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!user,
  });

  const createTransfer = useMutation({
    mutationFn: async (data: any) => {
      const booking = bookings?.find((b: any) => b.id === data.booking_id);
      const { error } = await supabase.from('booking_transfers').insert({
        booking_id: data.booking_id,
        transfer_type: data.transfer_type,
        from_unit: data.from_unit || booking?.plot_number || null,
        to_unit: data.to_unit || null,
        notes: data.notes || null,
        developer_user_id: user!.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-transfers'] });
      setAddOpen(false);
      setForm({ booking_id: '', transfer_type: '', from_unit: '', to_unit: '', notes: '' });
      toast.success('Transfer recorded');
    },
    onError: () => toast.error('Failed to create transfer'),
  });

  const updateTransferStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from('booking_transfers').update({ status: status as any }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-transfers'] });
      toast.success('Transfer status updated');
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h2 className="font-display text-xl font-bold">Booking Transfers</h2>
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-primary" /> Booking Transfers
          </h2>
          <p className="text-sm text-muted-foreground">Record ownership transfers, unit swaps, and cancellations.</p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1"><Plus className="h-4 w-4" /> New Transfer</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Record Transfer</DialogTitle></DialogHeader>
            <form onSubmit={e => { e.preventDefault(); createTransfer.mutate(form); }} className="space-y-3">
              <div>
                <Label className="text-xs">Booking *</Label>
                <Select value={form.booking_id} onValueChange={v => {
                  const bk = bookings?.find((b: any) => b.id === v);
                  setForm(f => ({ ...f, booking_id: v, from_unit: bk?.plot_number || '' }));
                }}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select booking" /></SelectTrigger>
                  <SelectContent>
                    {(bookings || []).map((b: any) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.customer_name} — Plot {b.plot_number || 'N/A'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Transfer Type *</Label>
                <Select value={form.transfer_type} onValueChange={v => setForm(f => ({ ...f, transfer_type: v }))}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ownership_transfer">Ownership Transfer</SelectItem>
                    <SelectItem value="unit_swap">Unit Swap</SelectItem>
                    <SelectItem value="cancellation">Cancellation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">From Unit</Label>
                  <Input className="h-9 text-sm" value={form.from_unit} onChange={e => setForm(f => ({ ...f, from_unit: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">To Unit</Label>
                  <Input className="h-9 text-sm" value={form.to_unit} onChange={e => setForm(f => ({ ...f, to_unit: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea rows={2} className="text-sm" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <Button type="submit" className="w-full" disabled={!form.booking_id || !form.transfer_type || createTransfer.isPending}>
                {createTransfer.isPending ? 'Recording...' : 'Record Transfer'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {(transfers || []).length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">No transfers recorded yet.</div>
      ) : (
        <div className="rounded-2xl border overflow-hidden bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(transfers || []).map((t: any) => (
                <TableRow key={t.id}>
                  <TableCell className="text-sm font-medium">
                    {t.bookings?.customer_name || '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-[10px]">
                      {TRANSFER_TYPE_LABELS[t.transfer_type] || t.transfer_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{t.from_unit || '—'}</TableCell>
                  <TableCell className="text-sm">{t.to_unit || '—'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(t.transfer_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Select
                      value={t.status}
                      onValueChange={(val) => updateTransferStatus.mutate({ id: t.id, status: val })}
                    >
                      <SelectTrigger className="h-7 w-[150px] text-[10px] border-0 p-0">
                        <Badge className={`text-[10px] ${TRANSFER_STATUS_COLORS[t.status] || ''}`}>
                          {TRANSFER_STATUS_LABELS[t.status] || t.status}
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>
                        {['pending', 'awaiting_bank_proof', 'completed', 'cancelled'].map(s => (
                          <SelectItem key={s} value={s} className="text-xs">{TRANSFER_STATUS_LABELS[s]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
